import { h as __h } from "preact";
import { signal } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */
import { Component, createRef } from "preact";
/** @import { RefObject, ComponentChildren } from "preact"; */

import html from "~/util/html.js";
import Icon from "~/component/Icon.js";
import input from "~/style/input.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Combobox.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Combobox.module.css", import.meta.url);
const css = adopt(sheet);

/** @typedef {{ id: string; label: string; render?: () => ComponentChildren }} Option */

/**
 * @extends {Component<ComboboxInputProps>}
 *
 * @typedef {object} ComboboxInputProps
 * @property {Pick<Signal<string>, "value" | "subscribe">} query
 * @property {number} [debounce]
 * @property {RefObject<ComboboxInput>} [ref]
 */
export class ComboboxInput extends Component {
  /** @type {(() => void) | undefined} */
  unsub;
  /** @override */
  componentDidMount() {
    this.unsub = this.props.query.subscribe(v => {
      this.q.value = v;
    });
  }

  /** @override */
  componentWillUnmount() {
    if (this.unsub) this.unsub();
    clearTimeout(this.#debounceTimer);
    this.q.value = "";
    this.props.query.value = "";
  }

  /** @type {number | undefined} */
  #debounceTimer = undefined;
  q = signal("");
  #onChange = () => {
    clearTimeout(this.#debounceTimer);
    if (this.props.debounce) {
      this.#debounceTimer = window.setTimeout(() => {
        this.props.query.value = this.q.value;
      }, this.props.debounce);
    } else {
      this.props.query.value = this.q.value;
    }
  };

  /** @param {InputEvent & { currentTarget: HTMLInputElement }} ev */
  #onSearchInput = ev => {
    const query = ev.currentTarget.value;
    this.q.value = query;
    this.#onChange();
  };
  render() {
    return __h("input", {
      "class": input.input,
      id: "search",
      name: "search",
      autocomplete: "off",
      autofocus: true,
      placeholder: "Search...",
      value: this.q.value,
      onInput: this.#onSearchInput
    });
  }
}

/**
 * @extends {Component<ComboboxProps>}
 *
 * @typedef {object} ComboboxProps
 * @property {Option[] | undefined} options
 * @property {boolean} loading
 * @property {Signal<Map<string, Option>>} [multiSelected]
 * @property {(option: Option) => void} [onSelect]
 * @property {number} [debounce]
 * @property {Signal<string>} query
 */
export default class Combobox extends Component {
  /** @type {RefObject<ComboboxInput>} */
  fieldRef = createRef();

  /** @param {Option} option */
  #onSelectOption = option => {
    if (this.props.multiSelected) {
      const next = new Map(this.props.multiSelected.value);
      if (next.has(option.id)) {
        next.delete(option.id);
      } else {
        next.set(option.id, option);
      }
      this.props.multiSelected.value = next;
    } else if (this.props.onSelect) {
      this.props.onSelect(option);
    }
  };

  /** @param {Option} option */
  #onDeselectOption = option => {
    if (!this.props.multiSelected) return;
    const next = new Map(this.props.multiSelected.value);
    next.delete(option.id);
    this.props.multiSelected.value = next;
  };
  render() {
    return __h("div", {
      "class": css.combobox
    }, __h(ComboboxInput, {
      ref: this.fieldRef,
      query: this.props.query,
      debounce: this.props.debounce
    }), this.renderSelected(), " ", this.renderResults());
  }
  renderSelected() {
    const selected = this.props.multiSelected;
    if (!selected) return;
    return __h("div", {
      "class": css.selected
    }, [...selected.value.values()].map(option => __h("span", {
      key: option.id
    }, __h("span", {}, option.label), __h("button", {
      "class": css.close,
      onClick: () => this.#onDeselectOption(option)
    }, __h(Icon, {
      name: "ex",
      size: 8,
      label: "dismiss"
    })))));
  }
  renderResults() {
    const options = this.props.options;
    if (!options) return;
    const selected = this.props.multiSelected ? this.props.multiSelected.value : null;
    return __h("div", {
      "class": css.results
    }, options.length === 0 ? __h("div", {
      "class": css.empty
    }, "No results found") : options.map(option => __h("button", {
      key: option.id,
      type: "button",
      onClick: () => this.#onSelectOption(option)
    }, __h("div", {
      "class": css.result
    }, option.render ? option.render() : option.label), selected && selected.has(option.id) && __h(Icon, {
      name: "check",
      size: 14
    }))));
  }
}